import {FC, useEffect, useCallback, ReactNode } from 'react';

interface ModalDialogProps {
    children: ReactNode;
    isShown: boolean;
    title: string;
    onClose: () => void;
}

const ModalDialog: FC<ModalDialogProps> = ({
                                                     children,
                                                     isShown,
                                                     title,
                                                     onClose
                                                 }) => {
    // Handle the escape key press
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Add/remove event listener for ESC key
    useEffect(() => {
        if (isShown) {
            document.addEventListener('keydown', handleKeyDown);
        }

        // Clean up the event listener when component unmounts or dialog closes
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isShown, handleKeyDown]);

    // If not shown, don't render anything
    if (!isShown) {
        return null;
    }

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 'auto',
            width: '100vw',
            maxHeight: '100vh',
            zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '4px',
                width: '90%',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                overflow: 'auto',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f8f8f8',
                }}>
                    <h3 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: 600,
                    }}>
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            padding: '0 4px',
                            color: '#666',
                        }}
                    >
                        &times;
                    </button>
                </div>
                <div style={{
                    padding: '16px',
                    height: '85vh',
                }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default ModalDialog;

// Usage example:
/*
import React, { useState } from 'react';
import ModalDialog from './ModalDialog';

const App: React.FC = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div>
      <button onClick={() => setShowModal(true)}>Open Modal</button>

      <ModalDialog
        isShown={showModal}
        title="Example Modal"
        onClose={() => setShowModal(false)}
      >
        <p>This is the content of the modal dialog.</p>
        <button onClick={() => setShowModal(false)}>Close</button>
      </ModalDialog>
    </div>
  );
};
*/